/**
 * Lineage Components for Quote → Job → Invoice Continuity
 * 
 * These components expose the lineage system through clear, guided UI.
 * Users cannot break lineage via the UI.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Link as LinkIcon, 
  ArrowRight, 
  CheckCircle, 
  X, 
  Briefcase, 
  ClipboardList,
  Receipt,
  Info,
  Loader2,
  AlertCircle,
  Lock,
  Plus,
  ChevronRight,
  Tag
} from 'lucide-react';
import GlassPanel from './GlassPanel';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ LINEAGE BANNER ============
export function LineageBanner({ 
  quoteId, 
  quoteRef,
  jobId,
  jobTitle,
  invoiceId,
  invoiceNumber,
  isImmutable = false 
}) {
  return (
    <div 
      className="lineage-banner"
      data-testid="lineage-banner"
      style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        marginBottom: 'var(--space-lg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 'var(--space-md)'
      }}
    >
      {/* Breadcrumb Trail */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <LinkIcon size={16} style={{ color: 'var(--accent-primary)' }} />
        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Lineage:</span>
        
        {quoteId && (
          <span 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              background: 'var(--glass-bg)',
              padding: '4px 8px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.8125rem',
              color: 'var(--accent-primary)'
            }}
          >
            <ClipboardList size={14} />
            Quote #{quoteRef || quoteId.slice(-4)}
            {isImmutable && <Lock size={12} style={{ color: 'var(--status-warning)' }} />}
          </span>
        )}
        
        {jobId && (
          <>
            <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
            <span 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                background: 'var(--glass-bg)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                color: 'var(--accent-primary)'
              }}
            >
              <Briefcase size={14} />
              {jobTitle || `Job #${jobId.slice(-4)}`}
            </span>
          </>
        )}
        
        {invoiceId && (
          <>
            <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
            <span 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                background: 'var(--glass-bg)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                color: 'var(--status-success)'
              }}
            >
              <Receipt size={14} />
              {invoiceNumber || `INV-${invoiceId.slice(-4)}`}
            </span>
          </>
        )}
      </div>
      
      {/* Info Text */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 'var(--space-xs)',
        fontSize: '0.75rem',
        color: 'var(--text-muted)'
      }}>
        <Info size={12} />
        All values are inherited. Variations are explicit.
      </div>
    </div>
  );
}

// ============ IMMUTABLE NOTICE ============
export function ImmutableNotice({ entityType = 'quote' }) {
  return (
    <div 
      data-testid="immutable-notice"
      style={{
        background: 'var(--status-warning-bg)',
        border: '1px solid var(--status-warning)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-sm) var(--space-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        marginBottom: 'var(--space-md)'
      }}
    >
      <Lock size={16} style={{ color: 'var(--status-warning)' }} />
      <span style={{ fontSize: '0.8125rem', color: 'var(--status-warning)' }}>
        This {entityType} is immutable - no edits allowed after acceptance.
      </span>
    </div>
  );
}

// ============ VARIATION BADGE ============
export function VariationBadge({ reason }) {
  return (
    <span 
      data-testid="variation-badge"
      title={reason}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'rgba(245, 158, 11, 0.2)',
        color: 'var(--status-warning)',
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.6875rem',
        fontWeight: '600',
        textTransform: 'uppercase'
      }}
    >
      <Tag size={10} />
      Variation
    </span>
  );
}

// ============ MODAL OVERLAY ============
function ModalOverlay({ children, onClose }) {
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-xl)'
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {children}
    </div>
  );
}

// ============ CREATE JOB FROM QUOTE MODAL ============
export function CreateJobFromQuoteModal({ isOpen, onClose, quote, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [jobTitle, setJobTitle] = useState('');
  const [targetStartDate, setTargetStartDate] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch quote line items
  useEffect(() => {
    if (isOpen && quote?.id) {
      setLoadingItems(true);
      setJobTitle(quote.title || quote.job_title || '');
      
      fetch(`${API_URL}/api/lineage/quotes/${quote.id}/line-items`, {
        headers: getAuthHeaders()
      })
        .then(res => res.json())
        .then(data => {
          setLineItems(Array.isArray(data) ? data : []);
          setLoadingItems(false);
        })
        .catch(err => {
          console.error('Error fetching line items:', err);
          setLoadingItems(false);
        });
    }
  }, [isOpen, quote, getAuthHeaders]);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/lineage/jobs/from-quote`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quote_id: quote.id,
          title: jobTitle || undefined,
          target_start_date: targetStartDate || undefined,
          notes: notes || undefined
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create job');
      }

      const result = await response.json();
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const totalNet = lineItems.reduce((sum, item) => sum + (item.line_total_net || 0), 0);

  return (
    <ModalOverlay onClose={onClose}>
      <div 
        data-testid="create-job-modal"
        style={{
          background: 'var(--bg-primary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--glass-border)',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
              Create Job from Quote
            </h2>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
              Step {step} of 2 - {step === 1 ? 'Review Quote' : 'Confirm Job Creation'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-lg)' }}>
          {error && (
            <div style={{
              background: 'var(--status-danger-bg)',
              border: '1px solid var(--status-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
              <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          {step === 1 && (
            <>
              {/* Quote Summary */}
              <GlassPanel className="mb-lg">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quote Reference</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-primary)' }}>
                      #{quote?.id?.slice(-4) || 'N/A'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</div>
                    <span className="status-badge completed">{quote?.status || 'ACCEPTED'}</span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Client</div>
                    <div style={{ color: 'var(--text-primary)' }}>{quote?.client_name || quote?.client || 'N/A'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total Value</div>
                    <div style={{ color: 'var(--status-success)', fontWeight: '600' }}>
                      £{(quote?.quote_amount_gross || quote?.amount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </GlassPanel>

              {/* Line Items */}
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-md)' }}>
                Line Items (Read-Only)
              </h3>
              
              {loadingItems ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xl)' }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
                </div>
              ) : lineItems.length > 0 ? (
                <div style={{ 
                  border: '1px solid var(--glass-border)', 
                  borderRadius: 'var(--radius-md)',
                  overflowX: 'auto',
                  marginBottom: 'var(--space-lg)'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--glass-bg)' }}>
                        <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Description</th>
                        <th style={{ textAlign: 'right', padding: 'var(--space-sm) var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Qty</th>
                        <th style={{ textAlign: 'right', padding: 'var(--space-sm) var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Price</th>
                        <th style={{ textAlign: 'right', padding: 'var(--space-sm) var(--space-md)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={item.id || idx} style={{ borderTop: '1px solid var(--glass-border)' }}>
                          <td style={{ padding: 'var(--space-sm) var(--space-md)', color: 'var(--text-primary)' }}>{item.description}</td>
                          <td style={{ padding: 'var(--space-sm) var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)' }}>{item.quantity}</td>
                          <td style={{ padding: 'var(--space-sm) var(--space-md)', textAlign: 'right', color: 'var(--text-secondary)' }}>£{(item.unit_price_net || 0).toFixed(2)}</td>
                          <td style={{ padding: 'var(--space-sm) var(--space-md)', textAlign: 'right', color: 'var(--text-primary)', fontWeight: '500' }}>£{(item.line_total_net || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid var(--glass-border)', background: 'var(--glass-bg)' }}>
                        <td colSpan="3" style={{ padding: 'var(--space-sm) var(--space-md)', textAlign: 'right', fontWeight: '600', color: 'var(--text-primary)' }}>Total (Net):</td>
                        <td style={{ padding: 'var(--space-sm) var(--space-md)', textAlign: 'right', fontWeight: '700', color: 'var(--accent-primary)' }}>£{totalNet.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div style={{ 
                  padding: 'var(--space-lg)', 
                  textAlign: 'center', 
                  color: 'var(--text-muted)',
                  background: 'var(--glass-bg)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-lg)'
                }}>
                  No line items found. Job will be created with quote totals.
                </div>
              )}

              {/* Info Notice */}
              <div style={{
                background: 'rgba(99, 102, 241, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-md)',
                display: 'flex',
                gap: 'var(--space-sm)'
              }}>
                <Info size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '2px' }} />
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  All line items will be automatically copied to the new Job. These values are inherited from the accepted Quote and cannot be modified. 
                  Any additional work must be added as explicit Variations.
                </span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Job Details Form */}
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Job Title (Optional)
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder={quote?.title || 'Enter job title...'}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Target Start Date (Optional)
                </label>
                <input
                  type="date"
                  value={targetStartDate}
                  onChange={(e) => setTargetStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes for this job..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Confirmation Summary */}
              <GlassPanel style={{ background: 'var(--status-success-bg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                  <CheckCircle size={18} style={{ color: 'var(--status-success)' }} />
                  <span style={{ fontWeight: '600', color: 'var(--status-success)' }}>Ready to Create Job</span>
                </div>
                <ul style={{ margin: 0, paddingLeft: '24px', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  <li>Job will be created from Quote #{quote?.id?.slice(-4)}</li>
                  <li>{lineItems.length} line item{lineItems.length !== 1 ? 's' : ''} will be inherited</li>
                  <li>Total value: £{(quote?.quote_amount_gross || quote?.amount || totalNet).toFixed(2)}</li>
                  <li>Source quote becomes the authoritative baseline</li>
                </ul>
              </GlassPanel>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--space-lg)',
          borderTop: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-md)'
        }}>
          {step === 1 ? (
            <>
              <button onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button onClick={() => setStep(2)} className="btn btn-primary">
                Continue <ArrowRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="btn btn-secondary">Back</button>
              <button 
                onClick={handleCreate} 
                disabled={loading}
                className="btn btn-primary"
                data-testid="confirm-create-job"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Briefcase size={16} />
                    Create Job
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============ CREATE INVOICE FROM JOB MODAL ============
export function CreateInvoiceFromJobModal({ isOpen, onClose, job, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [invoiceType, setInvoiceType] = useState('FINAL');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lineageInfo, setLineageInfo] = useState(null);

  // Fetch job line items and lineage
  useEffect(() => {
    if (isOpen && job?.id) {
      setLoadingItems(true);
      
      // Fetch line items
      Promise.all([
        fetch(`${API_URL}/api/lineage/jobs/${job.id}/line-items`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API_URL}/api/lineage/verify/job/${job.id}`, { headers: getAuthHeaders() }).then(r => r.json())
      ])
        .then(([items, lineage]) => {
          const itemsArray = Array.isArray(items) ? items : [];
          setLineItems(itemsArray);
          setLineageInfo(lineage);
          // Auto-select completed items
          const completedIds = itemsArray.filter(i => i.status === 'COMPLETED' && !i.invoiced).map(i => i.id);
          setSelectedItems(completedIds);
          setLoadingItems(false);
          
          // Set default due date to 14 days from now
          const defaultDue = new Date();
          defaultDue.setDate(defaultDue.getDate() + 14);
          setDueDate(defaultDue.toISOString().split('T')[0]);
        })
        .catch(err => {
          console.error('Error fetching job data:', err);
          setLoadingItems(false);
        });
    }
  }, [isOpen, job, getAuthHeaders]);

  const toggleItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/lineage/invoices/from-job`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: job.id,
          invoice_type: invoiceType,
          include_variations: true,
          line_item_ids: selectedItems.length > 0 ? selectedItems : null,
          due_date: new Date(dueDate).toISOString(),
          notes: notes || undefined
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create invoice');
      }

      const result = await response.json();
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedTotal = lineItems
    .filter(i => selectedItems.includes(i.id))
    .reduce((sum, i) => sum + (i.line_total_net || 0), 0);

  const hasLineage = lineageInfo?.has_quote_lineage;

  return (
    <ModalOverlay onClose={onClose}>
      <div 
        data-testid="create-invoice-modal"
        style={{
          background: 'var(--bg-primary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--glass-border)',
          maxWidth: '750px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
              Create Invoice from Job
            </h2>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
              Step {step} of 2 - {step === 1 ? 'Select Line Items' : 'Invoice Details'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-lg)' }}>
          {/* Lineage Warning */}
          {!hasLineage && (
            <div style={{
              background: 'var(--status-danger-bg)',
              border: '1px solid var(--status-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
              <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>
                This job has no source quote. Invoice creation requires Quote → Job → Invoice lineage.
              </span>
            </div>
          )}

          {error && (
            <div style={{
              background: 'var(--status-danger-bg)',
              border: '1px solid var(--status-danger)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-sm)'
            }}>
              <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
              <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{error}</span>
            </div>
          )}

          {/* Lineage Banner */}
          {hasLineage && (
            <LineageBanner 
              quoteId={lineageInfo?.quote_id}
              jobId={job?.id}
              jobTitle={job?.title}
              isImmutable={true}
            />
          )}

          {step === 1 && (
            <>
              {/* Job Summary */}
              <GlassPanel className="mb-lg">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--text-primary)' }}>{job?.title}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Job #{job?.id?.slice(-4)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Source Quote</div>
                    <div style={{ color: 'var(--accent-primary)', fontWeight: '500' }}>
                      #{lineageInfo?.quote_id?.slice(-4) || 'N/A'}
                    </div>
                  </div>
                </div>
              </GlassPanel>

              {/* Line Items Selection */}
              <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-primary)', marginBottom: 'var(--space-md)' }}>
                Select Items to Invoice
              </h3>

              {loadingItems ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-xl)' }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent-primary)' }} />
                </div>
              ) : lineItems.length > 0 ? (
                <div style={{ 
                  border: '1px solid var(--glass-border)', 
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  marginBottom: 'var(--space-lg)'
                }}>
                  {lineItems.map((item, idx) => {
                    const isDisabled = item.status === 'DEFERRED' || item.status === 'CANCELLED' || item.invoiced;
                    const isSelected = selectedItems.includes(item.id);
                    
                    return (
                      <div 
                        key={item.id || idx}
                        onClick={() => !isDisabled && toggleItem(item.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-md)',
                          padding: 'var(--space-md)',
                          borderBottom: idx < lineItems.length - 1 ? '1px solid var(--glass-border)' : 'none',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1,
                          background: isSelected ? 'rgba(99, 102, 241, 0.1)' : 'transparent'
                        }}
                      >
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => {}}
                          style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                            <span style={{ color: 'var(--text-primary)' }}>{item.description}</span>
                            {item.is_variation && <VariationBadge reason={item.variation_reason} />}
                            {item.invoiced && (
                              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                (Already invoiced)
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            Status: {item.status} • Qty: {item.quantity}
                          </div>
                        </div>
                        <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>
                          £{(item.line_total_net || 0).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ 
                  padding: 'var(--space-lg)', 
                  textAlign: 'center', 
                  color: 'var(--text-muted)',
                  background: 'var(--glass-bg)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: 'var(--space-lg)'
                }}>
                  No line items found for this job.
                </div>
              )}

              {/* Selection Summary */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-md)',
                background: 'var(--glass-bg)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
                </span>
                <span style={{ fontWeight: '700', fontSize: '1.125rem', color: 'var(--accent-primary)' }}>
                  £{selectedTotal.toFixed(2)}
                </span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Invoice Details Form */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                    Invoice Type
                  </label>
                  <select
                    value={invoiceType}
                    onChange={(e) => setInvoiceType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-sm) var(--space-md)',
                      background: 'var(--glass-bg)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  >
                    <option value="DEPOSIT">Deposit</option>
                    <option value="STAGE">Stage Payment</option>
                    <option value="FINAL">Final Invoice</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                    Due Date *
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: 'var(--space-sm) var(--space-md)',
                      background: 'var(--glass-bg)',
                      border: '1px solid var(--glass-border)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add invoice notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* Invoice Preview */}
              <GlassPanel style={{ background: 'var(--status-success-bg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
                  <Receipt size={18} style={{ color: 'var(--status-success)' }} />
                  <span style={{ fontWeight: '600', color: 'var(--status-success)' }}>Invoice Preview</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', fontSize: '0.875rem' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Source Job:</span>
                    <span style={{ color: 'var(--text-primary)', marginLeft: '8px' }}>{job?.title}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Source Quote:</span>
                    <span style={{ color: 'var(--text-primary)', marginLeft: '8px' }}>#{lineageInfo?.quote_id?.slice(-4)}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Type:</span>
                    <span style={{ color: 'var(--text-primary)', marginLeft: '8px' }}>{invoiceType}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Line Items:</span>
                    <span style={{ color: 'var(--text-primary)', marginLeft: '8px' }}>{selectedItems.length}</span>
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Amount (Net):</span>
                    <span style={{ color: 'var(--accent-primary)', fontWeight: '700', fontSize: '1.125rem', marginLeft: '8px' }}>
                      £{selectedTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </GlassPanel>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: 'var(--space-lg)',
          borderTop: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-md)'
        }}>
          {step === 1 ? (
            <>
              <button onClick={onClose} className="btn btn-secondary">Cancel</button>
              <button 
                onClick={() => setStep(2)} 
                disabled={selectedItems.length === 0 || !hasLineage}
                className="btn btn-primary"
              >
                Continue <ArrowRight size={16} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="btn btn-secondary">Back</button>
              <button 
                onClick={handleCreate} 
                disabled={loading || !dueDate}
                className="btn btn-primary"
                data-testid="confirm-create-invoice"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Receipt size={16} />
                    Create Invoice
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============ PROPOSE VARIATION MODAL ============
export function ProposeVariationModal({ isOpen, onClose, job, onSuccess }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState('');
  const [reason, setReason] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState('');
  const [category, setCategory] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/lineage/jobs/${job.id}/variations`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_id: job.id,
          description,
          reason,
          quantity: parseFloat(quantity),
          unit: 'item',
          unit_price_net: parseFloat(unitPrice),
          category: category || undefined
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to create variation');
      }

      const result = await response.json();
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const total = (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);

  return (
    <ModalOverlay onClose={onClose}>
      <div 
        data-testid="variation-modal"
        style={{
          background: 'var(--bg-primary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--glass-border)',
          maxWidth: '500px',
          width: '100%'
        }}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-lg)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
              Propose Variation
            </h2>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
              Additional work not in original quote
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-lg)' }}>
            {error && (
              <div style={{
                background: 'var(--status-danger-bg)',
                border: '1px solid var(--status-danger)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-md)',
                marginBottom: 'var(--space-lg)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <AlertCircle size={16} style={{ color: 'var(--status-danger)' }} />
                <span style={{ color: 'var(--status-danger)', fontSize: '0.875rem' }}>{error}</span>
              </div>
            )}

            {/* Info Notice */}
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-lg)',
              display: 'flex',
              gap: 'var(--space-sm)'
            }}>
              <Tag size={16} style={{ color: 'var(--status-warning)', flexShrink: 0, marginTop: '2px' }} />
              <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                Variations are clearly marked and tracked separately from original quote items. 
                They require approval before they can be invoiced.
              </span>
            </div>

            <div style={{ marginBottom: 'var(--space-md)' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                Description *
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder="What additional work is needed?"
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: 'var(--space-md)' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                Reason * (min 10 characters)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={10}
                placeholder="Why is this variation needed?"
                rows={2}
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)', marginBottom: 'var(--space-md)' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Quantity *
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  required
                  min="0.01"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                  Unit Price (Net) *
                </label>
                <input
                  type="number"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  required
                  min="0.01"
                  step="0.01"
                  placeholder="£"
                  style={{
                    width: '100%',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--glass-bg)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 'var(--space-xs)' }}>
                Category (Optional)
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Materials, Labour"
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  background: 'var(--glass-bg)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            {/* Total */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--space-md)',
              background: 'var(--glass-bg)',
              borderRadius: 'var(--radius-md)'
            }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: '500' }}>Variation Total:</span>
              <span style={{ fontWeight: '700', fontSize: '1.25rem', color: 'var(--status-warning)' }}>
                £{total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            padding: 'var(--space-lg)',
            borderTop: '1px solid var(--glass-border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 'var(--space-md)'
          }}>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
            <button 
              type="submit" 
              disabled={loading || !description || reason.length < 10 || !unitPrice}
              className="btn btn-primary"
              data-testid="submit-variation"
            >
              {loading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Submitting...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Propose Variation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

export default {
  LineageBanner,
  ImmutableNotice,
  VariationBadge,
  CreateJobFromQuoteModal,
  CreateInvoiceFromJobModal,
  ProposeVariationModal
};
